/**
 * Users API Module - User Management and Administration
 * Extracted from monolithic silo-api.ts for modular architecture
 */

import { type RequestFunction } from './types'
import type {
  User,
  UserCreateInput,
  UserUpdateInput,
  PaginatedResponse,
  FilterParams,
  Address,
  AddressCreateInput,
  AddressUpdateInput,
} from './types'

export class UsersApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // GENERAL USER MANAGEMENT
  // ============================================================================

  async getUsers(params?: FilterParams): Promise<PaginatedResponse<User>> {
    return this.request('/api/users/', { params })
  }

  async getUser(id: number): Promise<User> {
    return this.request(`/api/users/${id}/`)
  }

  async createUser(data: UserCreateInput): Promise<User> {
    return this.request('/api/users/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateUser(id: number, data: UserUpdateInput): Promise<User> {
    return this.request(`/api/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteUser(id: number): Promise<void> {
    // Use deactivation instead of hard delete for audit trail
    await this.deactivateUser(id)
  }

  // ============================================================================
  // USER ROLES MANAGEMENT
  // ============================================================================

  async getUserRoles(): Promise<Array<{ id: number; name: string; permissions: string[] }>> {
    return this.request('/api/users/roles/')
  }

  async assignUserRoles(userId: number, roleIds: number[]): Promise<User> {
    return this.request(`/api/users/${userId}/assign-roles/`, {
      method: 'POST',
      body: JSON.stringify({ role_ids: roleIds })
    })
  }

  // ============================================================================
  // ADMIN USER MANAGEMENT (specific admin user endpoints)
  // ============================================================================

  async getAdminUsers(params?: FilterParams): Promise<PaginatedResponse<User>> {
    const response = await this.request<User[] | PaginatedResponse<User>>('/api/users/', { params })
    
    // Handle both array and paginated response formats
    if (Array.isArray(response)) {
      return {
        count: response.length,
        next: null,
        previous: null,
        total_pages: 1,
        current_page: 1,
        page_size: response.length,
        results: response
      }
    }
    
    return response
  }

  async getAdminUser(id: number): Promise<User> {
    return this.request(`/api/users/${id}/`)
  }

  async createAdminUser(data: UserCreateInput): Promise<User> {
    return this.request('/api/users/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateAdminUser(id: number, data: UserUpdateInput): Promise<User> {
    return this.request(`/api/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteAdminUser(id: number): Promise<void> {
    return this.request(`/api/users/${id}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // USER LOOKUP AND VALIDATION
  // ============================================================================

  async checkUserExists(email: string): Promise<{ exists: boolean; user?: User }> {
    return this.request('/api/users/check-email/', { 
      params: { email }
    })
  }

  async searchUsers(query: string, params?: FilterParams): Promise<PaginatedResponse<User>> {
    return this.request('/api/users/', { 
      params: { 
        search: query,
        ...params 
      }
    })
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  async bulkUpdateUsers(userIds: number[], data: Partial<UserUpdateInput>): Promise<{ updated_count: number }> {
    return this.request('/api/users/bulk-update/', {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds,
        data
      })
    })
  }

  async bulkDeleteUsers(userIds: number[]): Promise<{ deleted_count: number }> {
    return this.request('/api/users/bulk-delete/', {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds
      })
    })
  }

  async bulkAssignRoles(userIds: number[], roleIds: number[]): Promise<{ updated_count: number }> {
    return this.request('/api/users/bulk-assign-roles/', {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds,
        role_ids: roleIds
      })
    })
  }

  // ============================================================================
  // USER STATISTICS AND ANALYTICS
  // ============================================================================

  async getUserStats(): Promise<{
    total_users: number
    active_users: number
    admin_users: number
    customer_users: number
    recent_signups: number
    by_role: Record<string, number>
  }> {
    return this.request('/api/users/stats/')
  }

  async getActiveUsers(params?: { days?: number; limit?: number }): Promise<User[]> {
    return this.request('/api/users/active/', { params })
  }

  async getRecentSignups(limit: number = 10): Promise<User[]> {
    return this.request('/api/users/recent-signups/', { 
      params: { limit }
    })
  }

  // ============================================================================
  // USER PERMISSIONS AND ACCESS CONTROL
  // ============================================================================

  async getUserPermissions(userId: number): Promise<string[]> {
    return this.request(`/api/users/${userId}/permissions/`)
  }

  async checkUserPermission(userId: number, permission: string): Promise<{ has_permission: boolean }> {
    return this.request(`/api/users/${userId}/check-permission/`, {
      params: { permission }
    })
  }

  async getUserGroups(userId: number): Promise<Array<{ id: number; name: string }>> {
    return this.request(`/api/users/${userId}/groups/`)
  }

  async assignUserToGroups(userId: number, groupIds: number[]): Promise<User> {
    return this.request(`/api/users/${userId}/assign-groups/`, {
      method: 'POST',
      body: JSON.stringify({ group_ids: groupIds })
    })
  }

  // ============================================================================
  // USER ACCOUNT MANAGEMENT
  // ============================================================================

  async grantPortalAccess(userId: number, grant: boolean): Promise<{
    message: string
    user_id: number
    email: string
    portal_access: boolean
    notification_sent: boolean
  }> {
    return this.request(`/api/users/${userId}/grant-portal-access/`, {
      method: 'POST',
      body: JSON.stringify({ grant })
    })
  }

  async activateUser(userId: number): Promise<User> {
    return this.request(`/api/users/${userId}/activate/`, {
      method: 'POST'
    })
  }

  async deactivateUser(userId: number): Promise<User> {
    return this.request(`/api/users/${userId}/deactivate/`, {
      method: 'POST'
    })
  }

  async resetUserPassword(userId: number): Promise<{ temporary_password: string }> {
    return this.request(`/api/users/${userId}/reset-password/`, {
      method: 'POST'
    })
  }

  async sendPasswordResetEmail(email: string): Promise<{ message: string }> {
    return this.request('/api/users/send-password-reset/', {
      method: 'POST',
      body: JSON.stringify({ email })
    })
  }

  // ============================================================================
  // USER PROFILE MANAGEMENT (Admin perspective)
  // ============================================================================

  async updateUserProfile(userId: number, profileData: {
    first_name?: string
    last_name?: string
    company?: string
    phone?: string
    timezone?: string
  }): Promise<User> {
    return this.request(`/api/users/${userId}/profile/`, {
      method: 'PATCH',
      body: JSON.stringify(profileData)
    })
  }

  async getUserActivity(userId: number, params?: {
    days?: number
    action_type?: string
    limit?: number
  }): Promise<Array<{
    id: number
    action: string
    timestamp: string
    details: Record<string, any>
  }>> {
    return this.request(`/api/users/${userId}/activity/`, { params })
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Helper to detect available fields for role-based UI
   */
  detectAvailableFields(data: User[]): Set<string> {
    const fields = new Set<string>()
    if (data.length > 0) {
      Object.keys(data[0]).forEach(key => fields.add(key))
    }
    return fields
  }

  /**
   * Helper to check if user data includes admin-only fields
   */
  isAdminUserData(user: User): boolean {
    return !!(user as any).is_staff || !!(user as any).is_superuser
  }

  /**
   * Format user display name
   */
  formatUserDisplayName(user: User): string {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim()
    }
    return user.username || user.email || 'Unknown User'
  }

  /**
   * Get user role names from user object
   */
  getUserRoleNames(user: User): string[] {
    // This assumes user object includes role information
    const roles: string[] = []
    
    if ((user as any).is_superuser) roles.push('Super Admin')
    if ((user as any).is_staff) roles.push('Staff')
    if ((user as any).groups && Array.isArray((user as any).groups)) {
      roles.push(...(user as any).groups.map((group: any) => group.name || group))
    }
    
    return roles.length > 0 ? roles : ['User']
  }

  // ============================================================================
  // USER FEATURE FLAGS
  // ============================================================================

  async getUserFeatures(userId: number): Promise<{ features: Record<string, boolean> }> {
    return this.request(`/api/users/${userId}/features/`)
  }

  async updateUserFeatures(userId: number, features: Record<string, boolean>): Promise<{
    message: string
    user_id: number
    email: string
    features: Record<string, boolean>
  }> {
    return this.request(`/api/users/${userId}/features/`, {
      method: 'PATCH',
      body: JSON.stringify(features)
    })
  }

  // ============================================================================
  // USER ADDRESS MANAGEMENT
  // ============================================================================

  /**
   * Get addresses for the authenticated user
   */
  async getUserAddresses(params?: any): Promise<PaginatedResponse<Address>> {
    return this.request('/api/users/addresses/', { params })
  }

  /**
   * Get a specific address for the authenticated user
   */
  async getUserAddress(id: number): Promise<Address> {
    return this.request(`/api/users/addresses/${id}/`)
  }

  /**
   * Create a new address for the authenticated user
   */
  async createUserAddress(data: AddressCreateInput): Promise<Address> {
    return this.request('/api/users/addresses/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Update an address for the authenticated user
   */
  async updateUserAddress(id: number, data: AddressUpdateInput): Promise<Address> {
    return this.request(`/api/users/addresses/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  /**
   * Archive an address for the authenticated user (soft delete)
   * Note: Currently uses the order address archive endpoint as user addresses are tied to orders
   */
  async deleteUserAddress(id: number): Promise<void> {
    // Use the order address archive endpoint since addresses are tied to orders
    return await this.request(`/api/ar/orders/address/${id}/archive/`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  }

  /**
   * Set an address as default for the authenticated user
   */
  async setUserDefaultAddress(id: number): Promise<Address> {
    return this.request(`/api/users/addresses/${id}/set-default/`, {
      method: 'POST'
    })
  }
}